import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, FileText, Plus, Users, LogOut, Copy, Send, Trash2, Check, X,
  Image as ImageIcon, Upload, Download, ReceiptText, Bell, KeyRound, Eye, EyeOff, Banknote,
} from "lucide-react";
import { api, API, authHeaders, getToken, clearToken, fmtMoney } from "@/lib/api";

const LEAD_STATUSES = ["New", "Contacted", "Quoted", "Won", "Lost"];
const STATUS_COLOR = {
  New: "text-white/70 border-white/25",
  Contacted: "text-blue-400 border-blue-400/40",
  Quoted: "text-gold border-gold/40",
  Won: "text-green-400 border-green-400/40",
  Lost: "text-red-400 border-red-400/40",
};

const STATUS_STYLES = {
  paid: "bg-green-500/15 text-green-400 border-green-500/30",
  partially_paid: "bg-gold/15 text-gold border-gold/30",
  sent: "bg-white/10 text-white/70 border-white/20",
};

function StatusBadge({ status }) {
  const label = status === "partially_paid" ? "Partial" : status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span className={`inline-block text-xs px-2.5 py-1 border ${STATUS_STYLES[status] || STATUS_STYLES.sent}`}>
      {label}
    </span>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-surface border border-line p-6">
      <p className="text-xs uppercase tracking-wider text-white/45">{label}</p>
      <p className="mt-2 font-heading font-bold text-2xl text-white tracking-tight">{value}</p>
    </div>
  );
}

const EMPTY_ITEM = { description: "", quantity: 1, unit_price: "" };

function CreateInvoice({ onCreated, prefill }) {
  const [form, setForm] = useState({
    customer_name: prefill?.customer_name || "", customer_email: prefill?.customer_email || "",
    customer_phone: prefill?.customer_phone || "", customer_address: prefill?.customer_address || "",
    tax_rate: "", deposit_amount: "", notes: "", send_now: true,
  });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updItem = (i, k) => (e) =>
    setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: e.target.value } : it)));

  const totals = useMemo(() => {
    const subtotal = items.reduce((s, it) => s + (parseFloat(it.unit_price) || 0) * (parseFloat(it.quantity) || 0), 0);
    const tax = subtotal * (parseFloat(form.tax_rate) || 0) / 100;
    return { subtotal, tax, total: subtotal + tax };
  }, [items, form.tax_rate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email) return toast.error("Customer name and email are required");
    const validItems = items.filter((it) => it.description && parseFloat(it.unit_price) >= 0);
    if (validItems.length === 0) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        customer_address: form.customer_address,
        line_items: validItems.map((it) => ({
          description: it.description,
          quantity: parseFloat(it.quantity) || 1,
          unit_price: parseFloat(it.unit_price) || 0,
        })),
        tax_rate: parseFloat(form.tax_rate) || 0,
        deposit_amount: form.deposit_amount ? parseFloat(form.deposit_amount) : null,
        notes: form.notes,
        send_now: form.send_now,
      };
      const { data } = await api.post("/invoices", payload, authHeaders());
      toast.success(
        data.email_sent ? "Invoice created & emailed to customer" : "Invoice created"
      );
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invoice");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-white/20 text-white placeholder-white/35 px-3.5 py-3 text-sm focus:outline-none focus:border-gold transition-colors";

  return (
    <form onSubmit={submit} data-testid="create-invoice-form" className="max-w-3xl space-y-8">
      <div>
        <h3 className="font-heading text-white text-lg mb-4">Customer</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <input data-testid="inv-customer-name" className={inputCls} placeholder="Customer name *" value={form.customer_name} onChange={upd("customer_name")} />
          <input data-testid="inv-customer-email" type="email" className={inputCls} placeholder="Customer email *" value={form.customer_email} onChange={upd("customer_email")} />
          <input data-testid="inv-customer-phone" className={inputCls} placeholder="Phone" value={form.customer_phone} onChange={upd("customer_phone")} />
          <input data-testid="inv-customer-address" className={inputCls} placeholder="Job address / city" value={form.customer_address} onChange={upd("customer_address")} />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-white text-lg">Line Items</h3>
          <button type="button" data-testid="add-line-item" onClick={() => setItems((a) => [...a, { ...EMPTY_ITEM }])} className="inline-flex items-center gap-1.5 text-gold text-sm hover:text-gold-hover transition-colors">
            <Plus size={16} /> Add item
          </button>
        </div>
        <div className="space-y-3">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 items-center">
              <input className={`${inputCls} col-span-6`} placeholder="Description" value={it.description} onChange={updItem(i, "description")} data-testid={`item-desc-${i}`} />
              <input className={`${inputCls} col-span-2`} type="number" min="0" step="1" placeholder="Qty" value={it.quantity} onChange={updItem(i, "quantity")} data-testid={`item-qty-${i}`} />
              <input className={`${inputCls} col-span-3`} type="number" min="0" step="0.01" placeholder="Unit $" value={it.unit_price} onChange={updItem(i, "unit_price")} data-testid={`item-price-${i}`} />
              <button type="button" onClick={() => setItems((a) => a.filter((_, idx) => idx !== i))} className="col-span-1 flex justify-center text-white/40 hover:text-red-400 transition-colors" aria-label="Remove">
                <X size={18} />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-white/45">Tax rate (%)</label>
          <input data-testid="inv-tax-rate" type="number" min="0" step="0.01" className={`${inputCls} mt-1.5`} placeholder="e.g. 8.25" value={form.tax_rate} onChange={upd("tax_rate")} />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-white/45">Deposit amount ($, optional)</label>
          <input data-testid="inv-deposit" type="number" min="0" step="0.01" className={`${inputCls} mt-1.5`} placeholder="e.g. 2000" value={form.deposit_amount} onChange={upd("deposit_amount")} />
        </div>
      </div>

      <textarea data-testid="inv-notes" rows={3} className={`${inputCls} resize-none`} placeholder="Notes (shown on invoice)" value={form.notes} onChange={upd("notes")} />

      <div className="bg-surface border border-line p-5 space-y-2 text-sm">
        <div className="flex justify-between text-white/70"><span>Subtotal</span><span>{fmtMoney(Math.round(totals.subtotal * 100))}</span></div>
        <div className="flex justify-between text-white/70"><span>Tax</span><span>{fmtMoney(Math.round(totals.tax * 100))}</span></div>
        <div className="flex justify-between text-white font-semibold text-base pt-2 border-t border-line"><span>Total</span><span className="text-gold">{fmtMoney(Math.round(totals.total * 100))}</span></div>
      </div>

      <label className="flex items-center gap-3 text-sm text-white/80 cursor-pointer">
        <input data-testid="inv-send-now" type="checkbox" checked={form.send_now} onChange={(e) => setForm((f) => ({ ...f, send_now: e.target.checked }))} className="h-4 w-4 accent-gold" />
        Email this invoice to the customer now
      </label>

      <button data-testid="inv-submit" type="submit" disabled={saving} className="bg-gold text-ink font-semibold px-8 py-4 hover:bg-gold-hover transition-colors disabled:opacity-60">
        {saving ? "Creating..." : "Create Invoice"}
      </button>
    </form>
  );
}

function GalleryManager({ items, onChange }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("showcase");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose an image first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("category", category);
      await api.post("/gallery", fd, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Photo added to gallery");
      setTitle(""); setFile(null);
      document.getElementById("gallery-file-input").value = "";
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo?")) return;
    try { await api.delete(`/gallery/${id}`, authHeaders()); toast.success("Removed"); onChange(); }
    catch { toast.error("Failed to remove"); }
  };

  const inputCls = "w-full bg-transparent border border-white/20 text-white placeholder-white/35 px-3.5 py-3 text-sm focus:outline-none focus:border-gold transition-colors";

  return (
    <div>
      <form onSubmit={upload} data-testid="gallery-upload-form" className="bg-surface border border-line p-6 mb-8 max-w-2xl">
        <h3 className="font-heading text-white text-lg mb-4">Add a project photo</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <input className={inputCls} placeholder="Title (e.g. Kitchen Remodel)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="gallery-title" />
          <select className={`${inputCls} cursor-pointer`} value={category} onChange={(e) => setCategory(e.target.value)} data-testid="gallery-category">
            <option value="showcase" className="bg-surface">Showcase</option>
            <option value="before" className="bg-surface">Before</option>
            <option value="after" className="bg-surface">After</option>
          </select>
        </div>
        <input id="gallery-file-input" type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} data-testid="gallery-file" className="mt-4 block w-full text-sm text-white/70 file:mr-4 file:py-2.5 file:px-4 file:border-0 file:bg-gold file:text-ink file:font-semibold file:cursor-pointer" />
        <button type="submit" disabled={uploading} data-testid="gallery-upload-submit" className="mt-4 inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 hover:bg-gold-hover transition-colors disabled:opacity-60">
          <Upload size={16} /> {uploading ? "Uploading..." : "Upload photo"}
        </button>
      </form>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="gallery-grid">
        {items.length === 0 && <p className="text-white/40 col-span-full">No photos yet. Upload your first project above.</p>}
        {items.map((it) => (
          <div key={it.id} className="relative group border border-line overflow-hidden">
            <img src={`${API}/gallery/${it.id}/image`} alt={it.title} className="w-full aspect-square object-cover" />
            <div className="absolute inset-0 bg-ink/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2 text-center">
              <span className="text-white text-xs">{it.title || "Untitled"}</span>
              <span className="text-gold text-[10px] uppercase tracking-wider">{it.category}</span>
              <button onClick={() => remove(it.id)} className="mt-1 inline-flex items-center gap-1 text-red-400 text-xs hover:text-red-300"><Trash2 size={14} /> Remove</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PaymentSettings() {
  const [username, setUsername] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/venmo", authHeaders())
      .then(({ data }) => setUsername(data.venmo_username || ""))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    const clean = username.trim().replace(/^@/, "");
    if (!clean) return toast.error("Enter your Venmo username");
    setBusy(true);
    try {
      await api.put("/admin/venmo", { venmo_username: clean }, authHeaders());
      setUsername(clean);
      toast.success("Venmo handle saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-transparent border border-white/20 text-white placeholder-white/35 px-3.5 py-3 text-sm focus:outline-none focus:border-gold transition-colors";

  return (
    <div className="max-w-md border-t border-line pt-8 mt-10" data-testid="payment-settings">
      <div className="flex items-center gap-2 mb-2"><Banknote size={18} className="text-gold" /><h3 className="font-heading text-white text-lg">Venmo Handle</h3></div>
      <p className="text-white/60 text-sm mb-6">Shown on customer invoices as a Venmo payment option. Enter your username without the @.</p>
      <form onSubmit={save} className="flex items-center gap-2">
        <div className="relative flex-1">
          <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40 text-sm">@</span>
          <input
            data-testid="venmo-username-input"
            className={`${inputCls} pl-8`}
            placeholder={loaded ? "your-venmo" : "Loading…"}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </div>
        <button type="submit" disabled={busy} className="bg-gold text-ink font-semibold px-5 py-3 hover:bg-gold-hover transition-colors disabled:opacity-60 whitespace-nowrap" data-testid="venmo-save">
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </div>
  );
}


function NotificationsSettings() {
  const [cfg, setCfg] = useState(null);
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [chats, setChats] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/telegram", authHeaders()); setCfg(data); setChatId(data.chat_id || ""); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const saveToken = async () => {
    if (!botToken.trim()) return toast.error("Paste your bot token");
    setBusy(true);
    try { await api.put("/admin/telegram", { bot_token: botToken.trim() }, authHeaders()); toast.success("Bot token saved"); setBotToken(""); load(); }
    catch { toast.error("Failed to save"); } finally { setBusy(false); }
  };
  const detect = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/telegram/resolve-chat", {}, authHeaders()); setChats(data.chats); if (data.chats.length === 0) toast.message("No chats found", { description: "Message your bot on Telegram first, then tap Detect again." }); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not detect"); } finally { setBusy(false); }
  };
  const saveChat = async (id) => {
    setBusy(true);
    try { await api.put("/admin/telegram", { chat_id: id }, authHeaders()); setChatId(id); toast.success("Chat connected"); load(); }
    catch { toast.error("Failed to save"); } finally { setBusy(false); }
  };
  const test = async () => {
    setBusy(true);
    try { await api.post("/admin/telegram/test", {}, authHeaders()); toast.success("Test alert sent — check Telegram!"); }
    catch (e) { toast.error(e.response?.data?.detail || "Test failed"); } finally { setBusy(false); }
  };

  const inputCls = "w-full bg-transparent border border-white/20 text-white placeholder-white/35 px-3.5 py-3 text-sm focus:outline-none focus:border-gold transition-colors";

  return (
    <div className="max-w-2xl" data-testid="notifications-settings">
      <div className="flex items-center gap-2 mb-2"><Bell size={18} className="text-gold" /><h3 className="font-heading text-white text-lg">Instant Telegram Alerts (free)</h3></div>
      <p className="text-white/60 text-sm mb-6">Get pinged on your phone the second a new lead arrives — 100% free.</p>

      <div className={`inline-flex items-center gap-2 mb-6 px-3 py-1.5 border text-sm ${cfg?.configured ? "border-green-400/40 text-green-400" : "border-white/20 text-white/50"}`} data-testid="telegram-status">
        {cfg?.configured ? <><Check size={14} /> Connected</> : "Not connected yet"}
      </div>

      <ol className="space-y-6">
        <li>
          <p className="text-white text-sm font-semibold mb-1">1. Create your bot</p>
          <p className="text-white/60 text-sm">In Telegram, message <span className="text-gold">@BotFather</span>, send <span className="text-gold">/newbot</span>, follow the prompts, and copy the token it gives you.</p>
        </li>
        <li>
          <p className="text-white text-sm font-semibold mb-2">2. Paste your bot token</p>
          <div className="flex gap-2">
            <input className={inputCls} placeholder="123456:ABC-DEF..." value={botToken} onChange={(e) => setBotToken(e.target.value)} data-testid="telegram-token-input" />
            <button onClick={saveToken} disabled={busy} className="bg-gold text-ink font-semibold px-5 hover:bg-gold-hover transition-colors disabled:opacity-60" data-testid="telegram-save-token">Save</button>
          </div>
        </li>
        <li>
          <p className="text-white text-sm font-semibold mb-2">3. Connect your chat</p>
          <p className="text-white/60 text-sm mb-2">Open your new bot in Telegram and send it any message (like "hi"). Then tap Detect.</p>
          <button onClick={detect} disabled={busy} className="border border-gold text-gold px-5 py-2.5 text-sm hover:bg-gold hover:text-ink transition-colors disabled:opacity-60" data-testid="telegram-detect">Detect my chat</button>
          {chats.length > 0 && (
            <div className="mt-3 space-y-2">
              {chats.map((c) => (
                <button key={c.id} onClick={() => saveChat(c.id)} className="block w-full text-left border border-line px-4 py-2.5 text-sm text-white/80 hover:border-gold transition-colors" data-testid={`telegram-chat-${c.id}`}>
                  {c.name} <span className="text-white/40">({c.id})</span> {chatId === c.id && <span className="text-green-400">✓ connected</span>}
                </button>
              ))}
            </div>
          )}
          {chatId && <p className="mt-2 text-xs text-white/50">Connected chat: {chatId}</p>}
        </li>
        <li>
          <p className="text-white text-sm font-semibold mb-2">4. Test it</p>
          <button onClick={test} disabled={busy || !cfg?.configured} className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-5 py-2.5 hover:bg-gold-hover transition-colors disabled:opacity-40" data-testid="telegram-test"><Send size={15} /> Send test alert</button>
        </li>
      </ol>
    </div>
  );
}

function RecordPaymentModal({ invoice, onClose, onSaved }) {
  const balanceCents = invoice.total - invoice.amount_paid;
  const [amount, setAmount] = useState((balanceCents / 100).toFixed(2));
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [sendReceipt, setSendReceipt] = useState(false);
  const [busy, setBusy] = useState(false);

  const inputCls = "w-full bg-transparent border border-white/20 text-white placeholder-white/35 px-3.5 py-3 text-sm focus:outline-none focus:border-gold transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      await api.post(`/invoices/${invoice.id}/record-payment`,
        { amount: amt, method, note, send_receipt: sendReceipt }, authHeaders());
      toast.success("Payment recorded");
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record payment");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="record-payment-modal">
      <div className="bg-ink border border-line w-full max-w-md p-7" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-1"><Banknote size={18} className="text-gold" /><h3 className="font-heading text-white text-lg">Record a Payment</h3></div>
        <p className="text-white/50 text-sm mb-1">{invoice.number} · {invoice.customer_name}</p>
        <p className="text-white/70 text-sm mb-6">Balance due: <span className="text-gold font-semibold">{fmtMoney(balanceCents)}</span></p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/45 mb-2">Amount received ($)</label>
            <input type="number" step="0.01" min="0" className={inputCls} value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="record-amount-input" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/45 mb-2">Method</label>
            <select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)} data-testid="record-method-select">
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="venmo">Venmo</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/45 mb-2">Note (optional)</label>
            <input className={inputCls} placeholder="e.g. Check #1024" value={note} onChange={(e) => setNote(e.target.value)} data-testid="record-note-input" />
          </div>
          <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
            <input type="checkbox" checked={sendReceipt} onChange={(e) => setSendReceipt(e.target.checked)} className="accent-gold" data-testid="record-send-receipt" />
            Email a receipt to the customer
          </label>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-white/20 text-white/70 px-4 py-3 hover:border-white/40 transition-colors">Cancel</button>
            <button type="submit" disabled={busy} className="flex-1 inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-4 py-3 hover:bg-gold-hover transition-colors disabled:opacity-60" data-testid="record-payment-submit">
              {busy ? "Saving…" : "Record Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AccountSettings() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const inputCls = "w-full bg-transparent border border-white/20 text-white placeholder-white/35 px-3.5 py-3 text-sm focus:outline-none focus:border-gold transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("New passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next }, authHeaders());
      toast.success("Password updated — use it next time you sign in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Could not update password");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md" data-testid="account-settings">
      <div className="flex items-center gap-2 mb-2"><KeyRound size={18} className="text-gold" /><h3 className="font-heading text-white text-lg">Change Password</h3></div>
      <p className="text-white/60 text-sm mb-6">Replace your temporary password with one only you know. Use at least 8 characters.</p>
      <form onSubmit={submit} className="space-y-4">
        <div className="relative">
          <label className="block text-xs uppercase tracking-wider text-white/45 mb-2">Current password</label>
          <input type={show ? "text" : "password"} className={inputCls} value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" data-testid="current-password-input" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-white/45 mb-2">New password</label>
          <input type={show ? "text" : "password"} className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" data-testid="new-password-input" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-white/45 mb-2">Confirm new password</label>
          <input type={show ? "text" : "password"} className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="confirm-password-input" />
        </div>
        <button type="button" onClick={() => setShow((s) => !s)} className="inline-flex items-center gap-2 text-xs text-white/50 hover:text-gold transition-colors" data-testid="toggle-password-visibility">
          {show ? <EyeOff size={14} /> : <Eye size={14} />} {show ? "Hide" : "Show"} passwords
        </button>
        <div>
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-6 py-3 hover:bg-gold-hover transition-colors disabled:opacity-60" data-testid="change-password-submit">
            <KeyRound size={15} /> {busy ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [leads, setLeads] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [prefill, setPrefill] = useState(null);
  const [openLead, setOpenLead] = useState(null);
  const [recordInv, setRecordInv] = useState(null);
  const [ready, setReady] = useState(false);

  const logout = () => { clearToken(); navigate("/admin/login"); };

  const loadAll = async () => {
    try {
      const [s, inv, ld, gal] = await Promise.all([
        api.get("/admin/stats", authHeaders()),
        api.get("/invoices", authHeaders()),
        api.get("/leads", authHeaders()),
        api.get("/gallery"),
      ]);
      setStats(s.data);
      setInvoices(inv.data);
      setLeads(ld.data);
      setGallery(gal.data);
      setReady(true);
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); navigate("/admin/login"); }
      else toast.error("Failed to load dashboard");
    }
  };

  useEffect(() => {
    if (!getToken()) { navigate("/admin/login"); return; }
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyLink = async (token) => {
    const url = `${window.location.origin}/invoice/${token}`;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("Payment link copied");
    } catch {
      toast.message("Payment link", { description: url });
    }
  };

  const resend = async (id) => {
    try { await api.post(`/invoices/${id}/send`, {}, authHeaders()); toast.success("Invoice re-sent"); }
    catch { toast.error("Failed to send"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    try { await api.delete(`/invoices/${id}`, authHeaders()); toast.success("Deleted"); loadAll(); }
    catch { toast.error("Failed to delete"); }
  };

  const invoiceFromLead = (lead) => {
    setPrefill({ customer_name: lead.name, customer_email: lead.email, customer_phone: lead.phone, customer_address: lead.location || "", _k: Date.now() });
    setTab("create");
  };

  const updateStatus = async (id, status) => {
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await api.patch(`/leads/${id}/status`, { status }, authHeaders()); }
    catch { toast.error("Failed to update status"); loadAll(); }
  };

  const TABS = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "invoices", label: "Invoices", icon: FileText },
    { id: "create", label: "New Invoice", icon: Plus },
    { id: "leads", label: "Leads", icon: Users },
    { id: "gallery", label: "Gallery", icon: ImageIcon },
    { id: "settings", label: "Alerts", icon: Bell },
    { id: "account", label: "Account", icon: KeyRound },
  ];

  return (
    <div className="min-h-screen bg-ink text-white font-body">
      <header className="border-b border-line sticky top-0 bg-ink/90 backdrop-blur-xl z-40">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="bg-white rounded p-1"><img src="/logo-white.png" alt="B3" className="h-9 w-auto object-contain" /></span>
            <span className="font-heading font-semibold tracking-tight hidden sm:block">Admin Dashboard</span>
          </div>
          <button data-testid="logout-button" onClick={logout} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-gold transition-colors">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-8">
        <div className="flex gap-2 mb-8 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => { if (t.id === "create") setPrefill(null); setTab(t.id); }}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm whitespace-nowrap border transition-colors ${
                tab === t.id ? "bg-gold text-ink border-gold font-semibold" : "border-line text-white/70 hover:border-gold hover:text-gold"
              }`}
            >
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {!ready && <p className="text-white/50">Loading…</p>}

        {ready && tab === "overview" && stats && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="overview-stats">
            <StatCard label="Total Collected" value={fmtMoney(stats.total_collected)} />
            <StatCard label="Outstanding" value={fmtMoney(stats.outstanding)} />
            <StatCard label="Total Invoiced" value={fmtMoney(stats.total_invoiced)} />
            <StatCard label="Invoices" value={stats.invoices} />
            <StatCard label="Paid Invoices" value={stats.paid_invoices} />
            <StatCard label="Leads" value={stats.leads} />
          </div>
        )}

        {ready && tab === "invoices" && (
          <div className="border border-line overflow-x-auto" data-testid="invoices-table">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-left text-white/45 border-b border-line">
                  <th className="p-4 font-medium">Invoice</th>
                  <th className="p-4 font-medium">Customer</th>
                  <th className="p-4 font-medium">Total</th>
                  <th className="p-4 font-medium">Paid</th>
                  <th className="p-4 font-medium">Status</th>
                  <th className="p-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-white/40">No invoices yet.</td></tr>
                )}
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-line/60 hover:bg-surface transition-colors">
                    <td className="p-4 font-heading text-gold">{inv.number}</td>
                    <td className="p-4">
                      <div className="text-white">{inv.customer_name}</div>
                      <div className="text-white/40 text-xs">{inv.customer_email}</div>
                    </td>
                    <td className="p-4 text-white/80">{fmtMoney(inv.total)}</td>
                    <td className="p-4 text-white/80">{fmtMoney(inv.amount_paid)}</td>
                    <td className="p-4"><StatusBadge status={inv.status} /></td>
                    <td className="p-4">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => copyLink(inv.token)} title="Copy pay link" className="p-2 text-white/50 hover:text-gold transition-colors" data-testid={`copy-${inv.number}`}><Copy size={16} /></button>
                        {inv.status !== "paid" && (
                          <button onClick={() => setRecordInv(inv)} title="Record cash/check/Venmo payment" className="p-2 text-white/50 hover:text-green-400 transition-colors" data-testid={`record-${inv.number}`}><Banknote size={16} /></button>
                        )}
                        <button onClick={() => resend(inv.id)} title="Re-send email" className="p-2 text-white/50 hover:text-gold transition-colors"><Send size={16} /></button>
                        <a href={`${API}/public/invoices/${inv.token}/pdf`} target="_blank" rel="noreferrer" title="Download PDF" className="p-2 text-white/50 hover:text-gold transition-colors" data-testid={`pdf-${inv.number}`}><Download size={16} /></a>
                        <a href={`/invoice/${inv.token}`} target="_blank" rel="noreferrer" title="View" className="p-2 text-white/50 hover:text-gold transition-colors">View</a>
                        <button onClick={() => del(inv.id)} title="Delete" className="p-2 text-white/50 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {ready && tab === "create" && (
          <CreateInvoice
            key={prefill?._k || "new"}
            prefill={prefill}
            onCreated={(data) => { loadAll(); setTab("invoices"); copyLink(data.invoice.token); }}
          />
        )}

        {ready && tab === "gallery" && (
          <GalleryManager items={gallery} onChange={loadAll} />
        )}

        {ready && tab === "settings" && <><NotificationsSettings /><PaymentSettings /></>}

        {ready && tab === "account" && <AccountSettings />}

        {ready && tab === "leads" && (
          <div className="border border-line overflow-x-auto" data-testid="leads-table">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-left text-white/45 border-b border-line">
                  <th className="p-4 font-medium">Name</th>
                  <th className="p-4 font-medium">Contact</th>
                  <th className="p-4 font-medium">Project</th>
                  <th className="p-4 font-medium">Status</th>
                  <th className="p-4 font-medium">Message</th>
                  <th className="p-4 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {leads.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-white/40">No leads yet.</td></tr>
                )}
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-line/60 align-top">
                    <td className="p-4 text-white">{l.name}</td>
                    <td className="p-4 text-white/70">
                      <a href={`tel:${l.phone}`} className="hover:text-gold">{l.phone}</a>
                      <div className="text-white/40 text-xs">{l.email}</div>
                    </td>
                    <td className="p-4 text-white/70">{l.project_type}<div className="text-white/40 text-xs">{l.location}</div></td>
                    <td className="p-4">
                      <select
                        value={l.status || "New"}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        data-testid={`status-select-${l.id}`}
                        className={`bg-transparent border px-2.5 py-1.5 text-xs cursor-pointer focus:outline-none ${STATUS_COLOR[l.status] || STATUS_COLOR.New}`}
                      >
                        {LEAD_STATUSES.map((s) => (
                          <option key={s} value={s} className="bg-surface text-white">{s}</option>
                        ))}
                      </select>
                    </td>
                    <td className="p-4 text-white/60 max-w-xs">
                      {l.message}
                      {l.intake_summary && (
                        <button onClick={() => setOpenLead(l)} data-testid={`view-intake-${l.id}`} className="mt-1 flex items-center gap-1 text-gold text-xs hover:text-gold-hover">
                          <ImageIcon size={12} /> View AI intake
                        </button>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <button onClick={() => invoiceFromLead(l)} data-testid={`lead-invoice-${l.id}`} className="inline-flex items-center gap-1.5 border border-gold text-gold text-xs px-3 py-2 hover:bg-gold hover:text-ink transition-colors whitespace-nowrap">
                        <ReceiptText size={14} /> Create Invoice
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {openLead && (
          <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm flex items-center justify-center p-5" onClick={() => setOpenLead(null)} data-testid="intake-modal">
            <div className="bg-surface border border-line max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-white text-lg">AI Intake — {openLead.name}</h3>
                <button onClick={() => setOpenLead(null)} className="text-white/50 hover:text-white"><X size={20} /></button>
              </div>
              {openLead.intake_summary && (
                <div className="bg-ink border-l-2 border-gold p-4 text-sm text-white/80 whitespace-pre-wrap mb-5">{openLead.intake_summary}</div>
              )}
              <div className="space-y-2">
                {(openLead.conversation || []).map((m, i) => (
                  <div key={i} className={`text-sm px-3 py-2 rounded-lg ${m.role === "user" ? "bg-gold/15 text-white ml-8" : "bg-ink border border-line text-white/80 mr-8"}`}>
                    <span className="block text-[10px] uppercase tracking-wider text-white/40 mb-0.5">{m.role === "user" ? "Customer" : "Assistant"}</span>
                    {m.content}
                  </div>
                ))}
                {(!openLead.conversation || openLead.conversation.length === 0) && <p className="text-white/40 text-sm">No conversation recorded.</p>}
              </div>
            </div>
          </div>
        )}

        {recordInv && (
          <RecordPaymentModal
            invoice={recordInv}
            onClose={() => setRecordInv(null)}
            onSaved={() => { setRecordInv(null); loadAll(); }}
          />
        )}
      </div>
    </div>
  );
}
